/**
 * What the onboarding wizard writes to the org it created, and the checks that make each write land there (#1144 review,
 * MEDIUM 2). The wizard keeps its state in the browser and resumes it, and the org switcher can change the active org
 * under it, while every call it makes (enable a pack, set the plant time, create a site and a gateway) goes to the active
 * org. A resumed wizard for org A with org B active would have written to B, a live org, and a clock write re-cuts that
 * org's last 400 days. So each write checks first that the active org is the wizard's, and stops with a message when it
 * is not. Pure functions over what they are given; the component holds the hooks.
 */
import { changeOf, type PlantTimeForm } from "@/lib/plant-time-form";
import type { PlantTime, PlantTimeChange } from "@/lib/shift-calendar";

/** The wizard's org is not the active one: nothing was written. */
export class WizardOrgError extends Error {
  constructor(orgName: string) {
    const named = orgName.trim() !== "";
    const name = named ? `"${orgName.trim()}"` : "the organisation you created";
    super(
      `This setup was started for ${name}, which is not the organisation open now. Nothing was changed. ` +
        `Switch back to ${named ? name : "it"} from the organisation menu to carry on.`,
    );
    this.name = "WizardOrgError";
  }
}

/**
 * Throws unless the active org is the one the wizard created. A live wizard past its second step has created an org; a
 * state with none, or with another one active, writes nothing.
 */
export function checkWizardOrg(wizard: { orgId: string | null; orgName: string }, activeOrgId: string | null): void {
  if (!wizard.orgId || wizard.orgId !== activeOrgId) throw new WizardOrgError(wizard.orgName);
}

/** The wizard's identity, as the checks read it. */
export interface WizardOrg {
  live: boolean;
  orgId: string | null;
  orgName: string;
}

/** Step 3: enable the picked pack on the wizard's org. Returns whether it called the platform. */
export async function enablePackStep(
  wizard: WizardOrg,
  activeOrgId: string | null,
  pickedPack: string | null,
  enable: (pack: string) => Promise<unknown>,
): Promise<boolean> {
  if (!wizard.live) return false;
  checkWizardOrg(wizard, activeOrgId);
  if (!pickedPack || pickedPack === "none") return false;
  await enable(pickedPack);
  return true;
}

/** What the Plant time step did. */
export interface PlantTimeStepResult {
  /** The PUT was sent. */
  sent: boolean;
  /** The plant time as the org holds it after the step, or null when the step did not read it. */
  plantTime: PlantTime | null;
}

/**
 * Step 4: set the wizard org's plant time from the form, sending only what differs from what the org has saved.
 *
 *   - Nothing for a stub build, and nothing when the person chose "Set this later".
 *   - Nothing when the form is unchanged since it was saved (`alreadySaved`; any edit clears it): sending it again would
 *     undo a later edit made on the Plant time page, and every Next would re-cut the org's days.
 *   - Otherwise the form is compared with the plant time the org holds now (a read), not with an empty one, and only the
 *     fields that differ are sent. Nothing differs, nothing is sent. The week start is never sent: the step does not ask
 *     for it.
 *
 * The caller has checked the active org (checkWizardOrg) before this reads or writes anything; it is checked again
 * here, so no path writes without it.
 */
export async function savePlantTimeStep(
  wizard: WizardOrg,
  activeOrgId: string | null,
  step: { later: boolean; alreadySaved: boolean; form: PlantTimeForm },
  org: { read: () => Promise<PlantTime>; save: (change: PlantTimeChange) => Promise<{ plant_time: PlantTime }> },
): Promise<PlantTimeStepResult> {
  if (!wizard.live || step.later) return { sent: false, plantTime: null };
  checkWizardOrg(wizard, activeOrgId);
  if (step.alreadySaved) return { sent: false, plantTime: null };
  const saved = await org.read();
  const change = changeOf(saved, step.form);
  delete change.production_week_start;
  if (Object.keys(change).length === 0) return { sent: false, plantTime: saved };
  const answer = await org.save(change);
  return { sent: true, plantTime: answer.plant_time };
}

/** A step's failure as the wizard says it: the wrong-org message as it is, or null to say it the usual way. */
export function wizardOrgErrorText(e: unknown): string | null {
  return e instanceof WizardOrgError ? e.message : null;
}
